import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { MatMenuTrigger } from '@angular/material/menu';
import { MatDrawer } from '@angular/material/sidenav';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { AccessibilityHelper } from '../../../shared/accessibility.helper';
import { ContextMenuComponent } from '../../../shared/components/context-menu/context-menu.component';
import { ButtonConfig } from '../../../shared/components/list-view/list-view-button-config.model';
import { TrashConfig, TrashDeleteEvent } from '../../../shared/components/list-view/list-view-trash-config.model';
import { ScriptableBaseComponent } from '../../../shared/components/scriptable-base/scriptable-base.component';
import { AlertDialogComponent, AlertDialogModel } from '../../../shared/dialogs/alert/alert-dialog.component';
import { IContextMenuItem } from '../../../shared/interfaces/icontext-menu-item.interface';
import { IListItem } from '../../../shared/interfaces/ilist-item.interface';
import { WorkflowErrorMessages } from '../../../shared/models/error-messages/workflow-error-messages';
import { DataCollection } from '../../../shared/models/site-content/data-collection.model';
import { Folder } from '../../../shared/models/site-content/folder.model';
import { FormInstancesAndFoldersUsingWorkflows } from '../../../shared/models/site-content/form-instance-using-workflowstate.model';
import { Workflow } from '../../../shared/models/site-content/workflow.model';
import { WorkflowsItemContextMenuData } from '../../../shared/models/workflows/item-context-menu';
import { CommunicationService } from '../../../shared/services/communication.service';
import { CurrentSiteService } from '../../../shared/services/current-site.service';
import { DataCollectionService } from '../../../shared/services/data-collection.service';
import { DeleteOnConfirmDialogService } from '../../../shared/services/delete-on-confirm-dialog.service';
import { FormInstanceService } from '../../../shared/services/form-instance.service';
import { WorkflowService } from '../../../shared/services/workflow.service';

// Constants.
const NEW_WORKFLOW: string = 'new workflow';
const EDIT_PROPERTIES: string = 'edit_properties';

@Component({
    selector: 'app-workflows',
    templateUrl: './workflows.component.html',
    styleUrls: ['./workflows.component.scss'],
    standalone: false
})
export class WorkflowsComponent extends ScriptableBaseComponent implements OnInit {
    // Instance data.
    private rootFolder: Folder;
    private siteWorkflows: Workflow[] = [];
    //private listItems: ListItem[];
    private buttonConfig: any;
    private showEditPropertiesDrawer: boolean = false;
    private selectedWorkflow: Workflow = null;
    private selectedWorkflowOriginalState: Workflow = null;

    // 04-15-2021:  added the following three properties.
    private contextMenuPosition = { x: '0px', y: '0px' };
    @ViewChild(MatMenuTrigger) contextMenu: MatMenuTrigger;
    @ViewChild('drawer') drawer: MatDrawer;
    private bUserCanDelete: boolean = false;

    private selectedItems: IListItem[];

    private itemContextMenuData: WorkflowsItemContextMenuData = null;
    @ViewChild('itemContextMenuComponent') itemContextMenuComponent: ContextMenuComponent;

    // Constructor.
    public constructor(
        private dataCollectionService: DataCollectionService,
        private workflowService: WorkflowService,
        private currentSiteService: CurrentSiteService,
        private communicationService: CommunicationService,
        private route: ActivatedRoute,
        private router: Router,
        private readonly titleService: Title,
        private formInstanceService: FormInstanceService, //VNEXT - 438: KLW - Logic to prevent deleting default workflows 
        public dialog: MatDialog,
        private deleteOnConfirmDialogService: DeleteOnConfirmDialogService,
        //componentHierarchyService: ComponentHierarchyService,
        elForBaseClass: ElementRef) {
        super(elForBaseClass);
        let toolbarNamesMap = {
            'trash': 'Delete',
            'sort_by_alpha': 'Sort',
            'view_module': 'View Mode'
        };
        this.setupSearchForListViewElements(toolbarNamesMap, { 'timeline': 'Workflow' });

        this.itemContextMenuData = new WorkflowsItemContextMenuData(this);
    }

    // Life cycle methods.
    public ngOnInit(): void {
        super.ngOnInit();

        // Build my list view configuration.
        this.buttonConfig = {
            right: [
                new ButtonConfig('New Workflow', NEW_WORKFLOW)
            ],
            item_actions: [
                new ButtonConfig('View Details', EDIT_PROPERTIES, 'info')
                //new ButtonConfig('Open for Editing', 'edit', 'edit')
            ]
        }

        if (this.buttonConfig?.right[0]) {
            this.buttonConfig.right[0].disabled = !this.site.siteIsBuildable;
        }
        else {
            console.log('this.buttonConfig not defined');
        }

        // If the user can administer a site,
        // allow that user to delete workflows.
        //
        // Note:  disabling this capability
        //        pending discussion.
        this.bUserCanDelete = this.site.siteIsAdministerable;
        if (this.bUserCanDelete) {
            this.buttonConfig['trash'] = new TrashConfig(true)
        }

        // Get the root folder and request the
        // list of workflows for the current site.
        this.rootFolder = this.site.folders[0];

        this.dataCollectionService.getWorkflowsBySite(this.site.id).then(responseWorkflows => {
            this.siteWorkflows = responseWorkflows;
        });

        this.titleService.setTitle(this.route.snapshot.data['title'] + ' for ' + this.site.name);
    }

    // Accessor methods called by my HTML code.
    public get ListItems(): IListItem[] {
        if (this.currentSiteService.Site.useSimplifiedPermissionsModel) {
            return (this.siteWorkflows.filter(wf => wf.isFormWorkflow));
        }
        else {
            return (this.siteWorkflows);
        }
    }

    public get ButtonConfig(): any {
        return (this.buttonConfig);
    }

    public get ContextMenuPositionX(): string {
        return (this.contextMenuPosition.x);
    }
    public get ContextMenuPositionY(): string {
        return (this.contextMenuPosition.y);
    }

    public get UserCanDelete(): boolean {
        return (this.bUserCanDelete);
    }

    public get ShowEditPropertiesDrawer(): boolean {
        return this.showEditPropertiesDrawer;
    }

    public get SelectedWorkflow(): Workflow {
        return this.selectedWorkflow;
    }

    public get DetailsDrawerTitle(): string {
        return 'Workflow Details';
    }

    public get ItemContextMenuData(): IContextMenuItem[] {
        return this.itemContextMenuData.ItemContextMenuData;
    }
    // End methods called by my HTML code.

    // Handle context menu events.
    public doOnItemContextMenu(event: any): void {
        // Set the context menu position.
        this.contextMenuPosition.x = event.originalEvent.clientX + 'px';
        this.contextMenuPosition.y = event.originalEvent.clientY + 'px';

        // Set menu data.
        this.contextMenu.menuData = { 'item': event.item };
        this.contextMenu.openMenu();

        /*
        let itemContextMenuPositionX = event.originalEvent.clientX + 'px';
        let itemContextMenuPositionY = event.originalEvent.clientY + 'px';
        if (this.itemContextMenuComponent != null)
            this.itemContextMenuComponent.openMenuForItemAt(event.item, itemContextMenuPositionX, itemContextMenuPositionY);
        */

        return;
    }

    // Handle control events.
    public doOnEdit(eventData: any): void {
        let id = eventData.item.id;
        let url = `/site/${this.site.id}/design-workflow/${id}`;
        let eventType = AccessibilityHelper.deduceEventType(eventData.originalEvent);
        this.router.navigateByUrl(url, { state: { originalEventType: eventType } }).then(x => {
            this.communicationService.emitChangeToSkipNavLink(url);
        });
    }

    public doOnItemAction(eventData: any): void {
        if (eventData.button.value == EDIT_PROPERTIES) {
            this.doOnEditProperties(eventData);
        } else if (eventData.button.value == 'edit') {
            this.doOnEdit(eventData);
        }
    }

    public doOnButtonClick(eventData: any): void {
        // Create a new workflow.
        if (eventData.button == NEW_WORKFLOW) {
            let url = `/site/${this.site.id}/design-workflow/0?folderId=${this.rootFolder.id}`;
            let eventType = AccessibilityHelper.deduceEventType(eventData.originalEvent);
            this.router.navigateByUrl(url, { state: { originalEventType: eventType } }).then(x => {
                this.communicationService.emitChangeToSkipNavLink(url);
            });
        }
    }

    public doOnEditProperties(eventData: any): void {
        this.selectedWorkflow = eventData.item;
        this.selectedWorkflowOriginalState = new Workflow();
        this.selectedWorkflowOriginalState.name = this.selectedWorkflow.name;
        this.selectedWorkflowOriginalState.description = this.selectedWorkflow.description;

        this.workflowService.get(this.selectedWorkflow.id).then(fullWorkflowDef => {
            // Save the full form def as the selected form.
            this.selectedWorkflow = fullWorkflowDef;

            // Open the properties drawer.
            this.showEditPropertiesDrawer = true;
            this.drawer.open();
        });
    }

    //VNEXT-365: KLW - Changing drop event to delete multiple workflows
    public doOnItemDelete(eventData: TrashDeleteEvent): void {
        let retVal = new Promise((resolve, reject) => {
            if (eventData.items) {
                let notDefault = true;
                let workflowIds = [];
                let defaultWorkflowsAttemptDelete = `<br/>`;

                //VNEXT - 438: KLW - Logic to prevent deleting default workflows 
                eventData.items.forEach(workflow => {
                    if (workflow.isDefaultFolderWorkflow ||
                        workflow.isDefaultFormWorkflow ||
                        workflow.isSystemWorkflow) {
                        notDefault = false;

                        defaultWorkflowsAttemptDelete += `<br/>${workflow.getName()}`;
                    }

                    workflowIds.push(workflow.getId());
                });

                if (notDefault && workflowIds.length > 0) {
                    this.findFormInstancesUsingWorkflow(workflowIds).then(response => {
                        let showAlertDialog = false;

                        if (response.fI_Names) {
                            if (response.fI_Names.length > 0) {
                                showAlertDialog = true;
                            }
                        }

                        if (response.folder_Names) {
                            if (response.folder_Names.length > 0) {
                                showAlertDialog = true;
                            }
                        }

                        if (!showAlertDialog) {
                            this.deleteOnConfirmDialogService.deleteOnConfirmMultipleItems(this.dialog, eventData.items, () => {

                                let promises: Promise<any>[] = [];

                                eventData.items.forEach(item => {
                                    let id: number = item.getId();

                                    let workflowPromise = this.deleteWorkflowServiceCall(id).then(response => {
                                        if (response) {
                                            resolve(null);
                                        }
                                    });

                                    promises.push(workflowPromise);
                                });

                                Promise.all(promises).then((x) => {
                                });
                            });
                        }
                        else {
                            let confirmationMessage = 'You are attempting to delete a workflow which is being used by a Form Instance or Folder.';
                            let additionalInformation: string = `For a more in-depth guide on solutions for this issue, please visit <a target="_blank" href="https://community.max.gov/x/71Ihiw">Deleting Workflow Associated with Form Instances or Folders</a>`;
                            let alertTitle: string = `Cannot Delete Workflow`;

                            this.alertDialog(alertTitle, confirmationMessage, additionalInformation);
                        }
                    });
                }
                else {
                    let confirmationMessage = 'You are not allowed to delete the following default workflow/s:' + defaultWorkflowsAttemptDelete;
                    let additionalInformation: string = `For a more in-depth guide on solutions for this issue, please visit <a target="_blank" href="https://community.max.gov/x/hGIhiw">Deleting Default Workflow</a>`;
                    let alertTitle: string = `Cannot Delete Workflow`;

                    this.alertDialog(alertTitle, confirmationMessage, additionalInformation);
                }
            }
        });
    }

    private alertDialog(alertTitle, confirmationMessage, additionalInformation) {
        let alertModel: AlertDialogModel;

        alertModel = new AlertDialogModel(alertTitle, confirmationMessage, null, additionalInformation);

        const dialogueRef = this.dialog.open(AlertDialogComponent, {
            data: alertModel
        });
        dialogueRef.afterClosed().subscribe(answer => {
            // Nothing to do.
        });
    }

    //VNEXT-365: KLW - Service call to delete workflow in the form of a Promise
    public deleteWorkflowServiceCall(id: number): Promise<boolean> {
        return new Promise<boolean>((resolve) => {
            this.workflowService.deleteFromUI(id).then(deletedWorkflowId => {
                this.siteWorkflows = this.siteWorkflows.filter(wf => wf.getId() !== deletedWorkflowId);
            });
        });
    }

    //VNEXT - 438: KLW - Logic to prevent deleting default workflows 
    private findFormInstancesUsingWorkflow(ids: number[]) {

        return new Promise<FormInstancesAndFoldersUsingWorkflows>((resolve) => {
            this.formInstanceService.getFormInstancesUsingWorkflows(ids).then((response: FormInstancesAndFoldersUsingWorkflows) => {
                if (response.fI_Names) {
                    let dialogConfig: MatDialogConfig = new MatDialogConfig();
                    //dialogConfig.width = '70%';
                    //dialogConfig.height = '85%';
                    dialogConfig.data = response;
                }

                resolve(response);
            });
        });
    }

    public doOnOpenItemClick(eventData: any): void {
        // Call the existing doOnEdit() method.
        let editParam = {
            'originalEvent': eventData,
            'item': this.contextMenu.menuData.item
        };
        this.doOnEdit(editParam);

        return;
    }

    //VNEXT-390: KLW - Method to set the items selected
    public setSelections(items: IListItem[]) {
        this.selectedItems = items;
    }

    //VNEXT-390: KLW - Fix multiple delete from context menu
    public doOnDeleteItemClick(eventData: any): void {

        if ((this.contextMenu != null) && (this.contextMenu.menuData != null)) {
            let trashEvent: TrashDeleteEvent = new TrashDeleteEvent(eventData, this.selectedItems);

            this.doOnItemDelete(trashEvent);
        }

        return;
    }

    public doOnWorkflowSave(eventData: any): void {
        this.closeDrawerClicked();

        let existingWorkflow: Workflow = this.siteWorkflows.find(wf => (wf.name == this.selectedWorkflow.name) && (wf.id != this.selectedWorkflow.id));

        // Does a workflow with this name already exist?
        if (existingWorkflow == null) {
            this.workflowService.update(this.selectedWorkflow).then(savedWorkflow => {
                //this.selectedWorkflow.copy(savedWorkflow);
                let workflowToUpdate: Workflow = this.siteWorkflows.find(wf => wf.id == savedWorkflow.id);
                if (workflowToUpdate) {
                    workflowToUpdate.name = savedWorkflow.name;
                    workflowToUpdate.description = savedWorkflow.description;
                }
            });
        } else {
            let message: string = WorkflowErrorMessages.duplicateWorkflowInSiteMessage(this.selectedWorkflow.name, this.site.id);
            this.dialog.open(AlertDialogComponent, {
                maxWidth: "600px",
                data: new AlertDialogModel("Cannot Save Workflow", message)
            });

            if ((this.selectedWorkflow != null) && (this.selectedWorkflowOriginalState != null)) {
                this.selectedWorkflow.name = this.selectedWorkflowOriginalState.name;
                this.selectedWorkflow.description = this.selectedWorkflowOriginalState.description;

                this.selectedWorkflowOriginalState = null;
            }
        }
    }

    public doOnWorkflowPropertiesClose(eventData: any): void {
        this.closeDrawerClicked();
    }

    public closeDrawerClicked(): void {
        this.showEditPropertiesDrawer = false;
        this.drawer.close();
    }

    public drawerClosing(): void {
        this.showEditPropertiesDrawer = false;
    }

    // Private helper methods.
    private get site(): DataCollection { // Not called by my HTML.
        return this.currentSiteService.Site;
    }
}
